/**
 * ReportsNotesTabContainer - Manages Reports & Notes tab state
 *
 * Container component that handles responsive layout logic for the
 * Reports & Notes tab.
 */

import { useWindowDimensions } from "react-native";

import ReportsNotesTabView from "./ReportsNotesTabView";

import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Derives the player display name for the downloaded PDF from the
 * backend-canonical `player.name` (SE-5469), matching what the player page
 * header shows — including compound surnames like "Virgil van Dijk".
 * @param player - Player data fragment
 * @returns Display name string or undefined
 */
const derivePlayerName = (
  player?: PlayerDetailPlayerMetaFragment | null
): string | undefined => player?.name ?? undefined;

/**
 * Props for ReportsNotesTabContainer component
 */
interface ReportsNotesTabContainerProps {
  /** Player data fragment */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
}

/**
 * Breakpoint for switching between the stacked and two-column layouts.
 * Width alone decides it on every platform (SE-7789): a native tablet at or
 * above it gets the two columns, the same as a web window of that width.
 */
const DESKTOP_BREAKPOINT = 768;

/**
 * ReportsNotesTabContainer manages state for Reports & Notes tab
 * @param props - Container props
 * @param props.player - Player data fragment
 * @returns ReportsNotesTabView with state and callbacks
 */
const ReportsNotesTabContainer = ({
  player,
}: ReportsNotesTabContainerProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;

  return (
    <ReportsNotesTabView
      isDark={isDark}
      player={player}
      playerName={derivePlayerName(player)}
      isDesktop={isDesktop}
    />
  );
};

export default ReportsNotesTabContainer;
